import React, { useEffect, useState } from "react";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";
import { SurveyReportAPI, surveyReportCSV } from "../../api/server";
import { downloadCSVBlob } from "../../common/downloadCSV";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";
import PaginatedItems from "./pagination";
import "./SurveyReport.css";

const DEFAULT_FILTERS = {
  range: "3Month",
  course: "DE",
  startDate: "",
  endDate: "",
};

const QUESTIONS = [
  {
    key: "debtStruggleDuration",
    label: "Q1",
    short: "Debt struggle duration",
  },
  {
    key: "helpfulInformation",
    label: "Q2",
    short: "Helpful information",
  },
  {
    key: "bankruptcyAdvice",
    label: "Q3",
    short: "Bankruptcy advice",
  },
];

const buildFilerRows = (row) => {
  const rows = [];
  if (row.mainAnswers) {
    rows.push({
      userId: row.userId,
      email: row.email,
      type: "Main",
      name: row.mainName,
      answers: row.mainAnswers,
      submittedAt: row.mainSubmittedAt,
    });
  }
  if (row.jointAnswers) {
    rows.push({
      userId: row.userId,
      email: row.email,
      type: "Joint",
      name: row.mainName,
      answers: row.jointAnswers,
      submittedAt: row.jointSubmittedAt,
    });
  }
  return rows;
};

const buildSortedTableRows = (reportData) =>
  reportData
    .flatMap(buildFilerRows)
    .sort(
      (first, second) =>
        new Date(second.submittedAt || 0).getTime() -
        new Date(first.submittedAt || 0).getTime()
    );

const SurveyReport = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [reportData, setReportData] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const tableRows = buildSortedTableRows(reportData);

  const fetchData = async (nextFilters, page = 1) => {
    try {
      setLoading(true);
      setError("");
      const response = await SurveyReportAPI(page, nextFilters);
      const data = response?.data?.data;
      setReportData(data?.result || []);
      setTotal(data?.total || 0);
      setPageNo(page);
      setAppliedFilters(nextFilters);
    } catch (requestError) {
      setReportData([]);
      setTotal(0);
      setError(
        requestError?.response?.data?.error || "Unable to load the survey report."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(DEFAULT_FILTERS, 1);
  }, []);

  const changeDateFilter = (range) => {
    const nextFilters = {
      ...filters,
      range,
      startDate: "",
      endDate: "",
    };
    setFilters(nextFilters);
    if (range !== "Date") {
      fetchData(nextFilters, 1);
    }
  };

  const applyCustomDate = (event) => {
    event.preventDefault();
    if (
      !filters.startDate ||
      !filters.endDate ||
      filters.startDate > filters.endDate
    ) {
      setError("Please select a valid start and end date.");
      return;
    }
    fetchData(filters, 1);
  };

  const exportData = async () => {
    try {
      setExportLoading(true);
      setError("");
      const response = await surveyReportCSV(appliedFilters);
      downloadCSVBlob(
        response.data,
        `Survey-Report-${appliedFilters.range}-DE.csv`
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error || "Unable to export the survey report."
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <DashBoardSidebar />
      <div id="content">
        <DashboardNavbar />
        <section className="manage-students language_wrap survey-report-page">
          {filters.range === "Date" && (
            <form className="survey-report-date-form" onSubmit={applyCustomDate}>
              <h6>Date Range:</h6>
              <label>
                <span>Start Date</span>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) =>
                    setFilters({ ...filters, startDate: event.target.value })
                  }
                />
              </label>
              <span className="survey-report-date-separator">To</span>
              <label>
                <span>End Date</span>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) =>
                    setFilters({ ...filters, endDate: event.target.value })
                  }
                />
              </label>
              <button
                type="submit"
                className={`btn_brnd survey-report-date-submit ${
                  loading ? "is-loading" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <ThreeDots height="20" width="55" color="#4fa94d" visible />
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          )}

          <div className="card survey-report-card">
            <div className="card-body">
              <div className="survey-report-heading">
                <h1>Survey Report</h1>
                <div className="survey-report-actions">
                  <button
                    type="button"
                    className={`btn_brnd survey-report-export ${
                      exportLoading ? "is-loading" : ""
                    }`}
                    onClick={exportData}
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <ThreeDots height="20" width="75" color="#4fa94d" visible />
                    ) : (
                      "Export Data"
                    )}
                  </button>

                  <div className="survey-report-select-wrap">
                    <select
                      className="form-select"
                      value={filters.range}
                      onChange={(event) => changeDateFilter(event.target.value)}
                      aria-label="Survey date range"
                    >
                      <option value="Today">Today</option>
                      <option value="Week">Week</option>
                      <option value="Month">Month</option>
                      <option value="3Month">3 Month</option>
                      <option value="Date">Custom Date</option>
                    </select>
                  </div>

                  <div
                    className="survey-report-select-wrap survey-report-course-tooltip"
                    data-tooltip="Survey reporting is currently available for the Debtor Education course only."
                    title="Survey reporting is currently available for the Debtor Education course only."
                  >
                    <select
                      className="form-select survey-report-course-select"
                      value="DE"
                      disabled
                      aria-label="Course: Debtor Education only"
                    >
                      <option value="DE">DE</option>
                    </select>
                  </div>
                </div>
              </div>

              {error && <div className="survey-report-error">{error}</div>}

              <div className="survey-report-table-wrap">
                <table className="table table-borderless survey-report-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      {QUESTIONS.map((question) => (
                        <th key={question.key}>
                          <strong>{question.label}</strong>
                          <small>{question.short}</small>
                        </th>
                      ))}
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="survey-report-empty">
                          <ThreeDots
                            height="42"
                            width="84"
                            color="#4fa94d"
                            ariaLabel="loading"
                            visible
                          />
                        </td>
                      </tr>
                    ) : tableRows.length ? (
                      tableRows.map((filer) => (
                          <tr
                            key={`${filer.userId}-${filer.type}`}
                            className={`survey-report-person-row ${
                              filer.type === "Joint" ? "survey-report-joint-row" : ""
                            }`}
                          >
                            <td className="survey-report-user">
                              <div className="survey-report-name-wrap">
                                <span>{filer.name || `${filer.type} Filer`}</span>
                                {filer.type === "Joint" && (
                                  <span className="survey-report-filer-badge">
                                    Joint
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="survey-report-email">
                              {filer.email || "N/A"}
                            </td>
                            {QUESTIONS.map((question) => (
                              <td className="survey-report-answer-cell" key={question.key}>
                                {filer.answers?.[question.key] || "N/A"}
                              </td>
                            ))}
                            <td className="survey-report-submitted">
                              {filer.submittedAt
                                ? moment(filer.submittedAt).format("MMM D, YYYY")
                                : "N/A"}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="survey-report-empty">
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && total > 20 && (
                <PaginatedItems
                  total={total}
                  pageNo={pageNo}
                  setPageNo={setPageNo}
                  pageSize={20}
                  handlePageChange={(page) => fetchData(appliedFilters, page)}
                />
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SurveyReport;
